import CallRoundedIcon from '@mui/icons-material/CallRounded';
import EmojiPeopleRoundedIcon from '@mui/icons-material/EmojiPeopleRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import HeadphonesRoundedIcon from '@mui/icons-material/HeadphonesRounded';
import { buildChapterExamplesPath, buildPracticeSectionPath } from '../../utils/contentRoutes';

/**
 * The shape of the practice journey. Both the map hub and the sidebar render
 * from this, so a level/section only has to be described once.
 *
 * `progressSection` is the index into the backend's TopicSection enum — the
 * same number the client posts to /progress. It must stay in step with
 * french-fide-be/src/lib/topicSections.ts.
 *
 * `itemCount` is only a placeholder for the first paint. The real total comes from
 * /api/practice/progress-manifest, which counts the ids the content actually
 * publishes — so a section grows a question without anyone editing this file.
 */
/**
 * The main-vocab.json topic each section teaches. These are the section-level
 * vocab sets (17/18/19), not the per-scenario `vocabId` on each item — the
 * Vocab and Flashcards tabs of a section show the section's own words.
 * Small Discussion has no dedicated vocab topic in main-vocab.json.
 */
export const SECTION_VOCAB_TOPIC = {
  intro: 17, // Se présenter / Sich vorstellen
  image: 18, // Décrire une image / Ein Bild beschreiben
  telephone: 19, // Téléphoner / Telefonieren
  discussion: null,
};

export const PROGRESS_SECTION = {
  DIALOGUE: 0,
  DISCUSSION: 1,
  USER_INTRO: 2,
  IMAGE_DESCRIPTION: 3,
  TELEPHONE_ROLEPLAY: 4,
  SMALL_DISCUSSION: 5,
  A1_DIALOGUE: 6,
  LISTENING: 7,
};

/**
 * Sections are named in the language being learned, matching the wording of the
 * vocab topic each one teaches. `name` mirrors main-vocab.json topics 17–19 (the
 * same static-mirror arrangement TOPIC_CATALOG uses — main-vocab.json is backend
 * content and isn't bundled). Small Discussion and the A1/B1 sections have no
 * vocab topic, so their names are authored here.
 *
 * `label` stays as the English identifier used in code and analytics. `name` is
 * the page heading; `navName` keeps map, sidebar, and footer labels compact.
 */
const A2_SECTIONS = [
  {
    slug: 'intro',
    label: 'Introduction',
    name: { fr: 'Introduction', de: 'Einleitung' },
    navName: { fr: 'Introduction', de: 'Einleitung' },
    kind: 'speaking',
    apiSection: 'intro',
    vocabTopicId: SECTION_VOCAB_TOPIC.intro,
    progressSection: PROGRESS_SECTION.USER_INTRO,
    itemCount: 5,
    // Not part of the official speaking structure — it's the opener the examiner
    // asks before it starts. So it heads the road as a stop without a number, and
    // the exam's own three steps are numbered 1–3 after it.
    prelude: true,
  },
  {
    slug: 'image',
    label: 'Image Description',
    name: { fr: 'Tâche 1 – Description d’une situation', de: 'Aufgabe 1 – Situationsbeschreibung' },
    navName: { fr: 'Description d’une situation', de: 'Situationsbeschreibung' },
    description: {
      fr: 'Décrire des personnes, des situations et des actions sur une image.',
      de: 'Personen, Situationen und Handlungen auf einem Bild beschreiben.',
    },
    kind: 'speaking',
    apiSection: 'image',
    vocabTopicId: SECTION_VOCAB_TOPIC.image,
    progressSection: PROGRESS_SECTION.IMAGE_DESCRIPTION,
    itemCount: 5,
  },
  {
    slug: 'telephone-roleplay',
    label: 'Telephone Dialogue',
    name: { fr: 'Tâche 2 – Conversation téléphonique (simulation)', de: 'Aufgabe 2 – Telefonat (Simulation)' },
    navName: { fr: 'Conversation téléphonique', de: 'Telefonat' },
    // The full French heading wraps to three lines on phones.
    mobileName: { fr: 'Tâche 2 – Conversation téléphonique' },
    description: {
      fr: 'Prendre, reporter ou annuler un rendez-vous par téléphone.',
      de: 'Telefonisch einen Termin vereinbaren, verschieben oder absagen.',
    },
    kind: 'speaking',
    apiSection: 'telephone',
    vocabTopicId: SECTION_VOCAB_TOPIC.telephone,
    progressSection: PROGRESS_SECTION.TELEPHONE_ROLEPLAY,
    itemCount: 5,
    // Beyond the 5 curated calls, the full dialogue catalog lives under this section.
    exploreTrack: 'dialogue',
  },
  {
    slug: 'discussion',
    label: 'Small Discussion',
    name: { fr: 'Tâche 3 – Discussion', de: 'Aufgabe 3 – Gespräch' },
    navName: { fr: 'Discussion', de: 'Gespräch' },
    description: {
      fr: 'Parler d’habitudes et de préférences, décrire une expérience ou un événement, et décrire un processus ou un comportement.',
      de: 'Über Gewohnheiten und Vorlieben sprechen, ein Erlebnis oder ein Ereignis schildern und einen Vorgang oder ein Verhalten schildern.',
    },
    kind: 'speaking',
    apiSection: 'discussion',
    vocabTopicId: SECTION_VOCAB_TOPIC.discussion,
    progressSection: PROGRESS_SECTION.SMALL_DISCUSSION,
    itemCount: 5,
    // Each practice set has three independently ticked questions. Progress for this
    // task is a completed set, not an individual question.
    progressGroupSize: 3,
  },
];

/**
 * The A1 situations, a static mirror of a1_speaking_topics.json — the same
 * arrangement TOPIC_CATALOG uses, so the sidebar can list them without waiting
 * for the section fetch. The page itself renders the titles the API returns.
 */
export const A1_SITUATIONS = [
  { id: 1, name: { fr: 'Dans un magasin', de: 'Im Kleidergeschäft' } },
  { id: 2, name: { fr: 'Au café', de: 'Im Café' } },
];

/**
 * Each situation is ticked off in two halves — the pictures, then the dialogue. The
 * ids for those halves are authored in a1_speaking_topics.json and arrive on the item
 * as `progressIds`; this is only how many halves a situation has, for the counts the
 * sidebar and map draw before the section has loaded.
 */
export const A1_PARTS = 2;

/**
 * A1 is one section holding two situations, each its own page. There is no
 * separate image-description exercise: describing the four pictures is the first
 * half of a situation, the guided dialogue built on them is the second.
 */
const A1_SECTIONS = [
  {
    slug: 'dialogue',
    label: 'Dialogue',
    name: { fr: 'Dialogue', de: 'Dialog' },
    // A1 is one page holding both exam tasks, so the subtitle sums up the pair; each
    // task keeps its own official title above its half.
    description: {
      fr: 'Nommer des lieux, des situations et des actions sur des images, puis réagir dans des situations de tous les jours.',
      de: 'Orte, Situationen und Handlungen auf Bildern benennen und in alltäglichen Situationen reagieren.',
    },
    tasks: {
      situationDescription: {
        title: { fr: 'Tâche 1 – Description d’une situation', de: 'Aufgabe 1 – Situationsbeschreibung' },
        description: {
          fr: 'Désigner des lieux, des situations communicatives et des actions sur des images.',
          de: 'Orte, kommunikative Situationen und Handlungen auf Bildern benennen.',
        },
      },
      interaction: {
        title: { fr: 'Tâche 2 – Interaction', de: 'Aufgabe 2 – Interaktion' },
        description: {
          fr: 'Réagir dans des situations de contact fréquentes.',
          de: 'In alltäglichen Kontaktsituationen reagieren.',
        },
      },
    },
    kind: 'a1-dialogue',
    apiLevel: 'a1',
    apiSection: 'dialogue',
    progressSection: PROGRESS_SECTION.A1_DIALOGUE,
    itemCount: A1_SITUATIONS.length * A1_PARTS,
  },
];

const B1_SECTIONS = [
  {
    slug: 'discussion',
    label: 'Discussion',
    name: { fr: 'Discussion', de: 'Gespräch' },
    // The four things the B1 exam asks of the chosen subject, said in one line —
    // the choice itself is explained by the note beside the catalog.
    description: {
      fr: 'Raconter une expérience, expliquer un processus, parler des côtés positifs et négatifs d’une situation, et prendre position sur une situation hypothétique.',
      de: 'Ein Erlebnis schildern, einen Ablauf darstellen, positive und negative Seiten einer Situation schildern und zu einer hypothetischen Situation Stellung nehmen.',
    },
    // B1 has no curated layer — the section *is* the discussion catalog.
    kind: 'catalog',
    track: 'discussion',
    progressSection: PROGRESS_SECTION.DISCUSSION,
  },
];

export const PRACTICE_LEVELS = [
  { id: 'a1', label: 'A1', sections: A1_SECTIONS },
  { id: 'a2', label: 'A2', sections: A2_SECTIONS, primary: true },
  { id: 'b1', label: 'B1', sections: B1_SECTIONS },
];

export const getLevel = (levelId) => PRACTICE_LEVELS.find((level) => level.id === levelId) ?? null;

export const pickLang = (map, lang = 'fr') => map?.[lang] ?? map?.fr ?? '';

/** A glanceable icon per section slug, shared by the map's stops and the sidebar. */
export const SECTION_ICON = {
  intro: EmojiPeopleRoundedIcon,
  image: ImageRoundedIcon,
  telephone: CallRoundedIcon,
  'telephone-roleplay': CallRoundedIcon,
  discussion: ForumRoundedIcon,
  dialogue: QuestionAnswerRoundedIcon,
  listening: HeadphonesRoundedIcon,
};

export const getSectionIcon = (section) =>
  SECTION_ICON[section?.slug] ?? SECTION_ICON[section?.kind] ?? ForumRoundedIcon;

/**
 * The number a section wears on the road, or null to wear its own icon instead.
 *
 * A level only numbers its steps when it has an order worth counting: A1 and B1
 * are a single exercise each, and "1" beside a lone row says nothing its icon
 * doesn't. Prelude sections stay unnumbered, and the steps after one close the
 * gap it leaves — so A2 reads as an opener followed by exam steps 1, 2, 3.
 */
export const getStepNumber = (sections, index) => {
  const numbered = sections.filter((entry) => !entry.prelude);
  if (numbered.length < 2 || sections[index]?.prelude) return null;
  return index + 1 - sections.slice(0, index).filter((entry) => entry.prelude).length;
};

/** What a learner reads for a section: its name in the study language, else the English label. */
export const getSectionName = (section, lang = 'fr') => pickLang(section?.name, lang) || section?.label || '';

/** Shorter phone heading, a prefix of the full name. No French fallback: German keeps its own heading. */
export const getSectionMobileName = (section, lang = 'fr') => section?.mobileName?.[lang] || '';

export const getSectionNavName = (section, lang = 'fr') =>
  pickLang(section?.navName, lang) || getSectionName(section, lang);

export const getSectionDescription = (section, lang = 'fr') => pickLang(section?.description, lang);

export const getSection = (levelId, sectionSlug) =>
  getLevel(levelId)?.sections.find((section) => section.slug === sectionSlug) ?? null;

/** Where a sidebar/map entry navigates to. Catalog sections jump into the existing catalog. */
export const getSectionPath = (lang, levelId, section) =>
  section.kind === 'catalog'
    ? buildChapterExamplesPath(lang, section.track)
    : buildPracticeSectionPath(lang, levelId, section.slug);
